import { useEffect, useState, useContext } from 'react';
import { gql, ApolloClient } from '@apollo/client';

import { FeatureArticleContext } from './Feature.context';
import { Article, Issue, mapIssuesNonImplementedFeatures, reduceExcludeComponents } from '../../constants';
import { AppContext } from '../../App.context';
import { Db } from '../../services/Db';

export function useFeatureArticle(args: GetUseIssueArgs): GetIssueResponse {
    const { articleId, issueId } = args;
    const { apolloClient, onlineStatus, sw, db } = useContext(AppContext);
    const { article, setArticle } = useContext(FeatureArticleContext);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    useEffect(() => {
        if (!articleId) {
            return;
        }
        if (!issueId) {
            return;
        }
        if (!apolloClient) {
            return;
        }

        if (onlineStatus === null) {
            return;
        }

        if (!db) {
            return;
        }

        getFeatureArticle({
            issueId,
            apolloClient,
            onlineStatus,
            db,
            sw,
            articleId
        })
            .then(({ article }) => {
                setArticle(article);
            })
            .catch((err) => {
                setError(err);
            })
            .finally(() => {
                setIsLoading(false)
            });
    }, [apolloClient, onlineStatus, sw, issueId, articleId]);

    return {
        article,
        isLoading,
        error
    }
}

interface GetUseIssueArgs {
    articleId: string | undefined;
    issueId: string | undefined;
}

interface GetIssueResponse {
    article: Article | null;
    isLoading: boolean;
    error?: Error | null;
}

async function getFeatureArticle(args: GetIssueArgs): Promise<FeatureArticleResponse> {
    const { onlineStatus } = args;
    if (onlineStatus) {
        return getRemoteIssue(args);
    }
    return getLocalIssue(args);
}

async function getRemoteIssue(args: GetIssueArgs): Promise<FeatureArticleResponse> {
    const { issueId, apolloClient, db, articleId, sw } = args;
    const isSlugIssue = isNaN(issueId);

    const result = await apolloClient.query<any>({
        query: GET_ISSUE,
        fetchPolicy: 'no-cache',
        variables: {
            id: issueId,
            isSlug: isSlugIssue
        },
    });

    const response: FeatureArticleResponse = {
        issue: null,
        article: null
    }

    let { issue } = result.data;
    if (issue && issue['__typename']) {
        delete issue['__typename'];
    }
    if (!issue) {
        return response;
    }

    // TODO Remove this to support features
    issue = mapIssuesNonImplementedFeatures(issue)

    response.issue = issue;
    const isSlug = isNaN(articleId);
    for (const article of issue.featuredArticles) {
        const { id, slug } = article;
        article.IssueID = `${issue.id}`;
        if (issue.slug) {
            article.issueSlug = `${issue.slug}`;
        }
        if (isSlug) {
            if (`${articleId}` === `${slug}`) {
                response.article = article;
                break;
            }
        } else {
            if (`${issueId}` === `${id}`) {
                response.article = article;
                break;
            }
        }
    }
    await saveIssueInCache({ issue, db });
    if (response.article) {
        await saveArticleInCache({
            article: response.article,
            db,
            sw
        })
    }
    return response;
}

interface FeatureArticleResponse {
    issue: Issue | null;
    article: Article | null;
}

async function saveIssueInCache(args: { issue: any; db: Db }): Promise<Issue | null> {
    const { issue, db } = args;
    const cachedIssue = await db.getIssue(issue.id, false);
    if (cachedIssue) {
        cachedIssue.name = issue.name;
        cachedIssue.description = issue.description;
        cachedIssue.cover = issue.cover;
        if (cachedIssue.featuredArticles) {
            delete cachedIssue.featuredArticles;
        }
        return db.saveIssue(clone(cachedIssue));

    }
    return db.saveIssue(clone(issue));
}

async function saveArticleInCache(args: { article: any; db: Db, sw: ServiceWorker | null | undefined }): Promise<Article | null> {
    const { article, db, sw } = args;
    if (article.components) {
        article.components = article.components
            .reduce(reduceExcludeComponents, []);
    }
    await db.saveArticles([clone(article)]);
    if (sw) {
        const images: Set<string> = new Set();
        getImagesFromArticle(article.components, images);
        if (images.size) {
            sw.postMessage({
                type: 'DOWNLOAD_BACKGROUND_IMAGES',
                payload: {
                    images: [...images],
                },
            });
        }
    }
    return article;
}

function getImagesFromArticle(components: Array<any>, images: Set<string>) {
    for (const component of components) {
        const { imageurl, columns, content } = component;
        if (imageurl) {
            images.add(imageurl);
        }

        if (columns && Array.isArray(columns)) {
            for (const col of columns) {
                getImagesFromArticle(col, images);
            }
            continue;
        }

        if (content && Array.isArray(images)) {
            getImagesFromArticle(content, images);
            continue;
        }

        if (component.images && Array.isArray(component.images)) {
            for (const image of component.images) {
                images.add(image);
            }
            continue;
        }

    }
}

async function getLocalIssue(args: GetIssueArgs): Promise<FeatureArticleResponse> {
    const { issueId, db, articleId } = args;
    const isSlug = isNaN(issueId);
    const response: FeatureArticleResponse = {
        issue: null,
        article: null
    }
    const issue = await db.getIssue(issueId, isSlug);
    if (!issue) {
        return response;
    }

    response.issue = issue;

    const articles = await db.getArticlesByIssue(issue.id);
    for (const article of articles) {
        const { id, slug } = article;
        if (isSlug) {
            if (`${articleId}` === `${slug}`) {
                response.article = article;
                break;
            }
        } else {
            if (`${articleId}` === `${id}`) {
                response.article = article;
                break;
            }
        }
    }

    return response
}

interface GetIssueArgs {
    issueId: any;
    articleId: any;
    apolloClient: ApolloClient<any>;
    onlineStatus: boolean;
    db: Db;
    sw?: ServiceWorker | null;
}


const GET_ARTICLE_BY_ID = gql`
    query GetArticle($id: ID!) {
   		article(id: $id) {
            id
            name
            thumbnail
            index
            channel
            style
            slug
            audio
            ArticleID
            components
            pages
        }
    }
`;


export const GET_ISSUE = gql`
	 query GetIssue($id: ID!, $isSlug: Boolean!) {
        issue(id: $id, isSlug: $isSlug) {
          	id
            name
            description
            externalUrl
            textDirection
            cover
            slug
            releaseDate
            updated
            created
            isPurchased
            featuredArticles {
                id
                name
                ArticleID
                style
                keywords
                description
                featureImage	
                role
                slug
                channel	
                styleProperties
                created	
                components
                isFeatured
            }
        }
}
`;

// TODO Revert back to support features
export const HACK_GET_ISSUE = gql`
	 query GetIssue($id: ID!, $isSlug: Boolean!) {
        issue(id: $id, isSlug: $isSlug) {
          	id
            name
            description
            externalUrl
            textDirection
            cover
            slug
            releaseDate
            features
            updated
            created
            isPurchased
            featuredArticles {
                id
                name
                ArticleID
                style
                keywords
                description
                featureImage	
                role
                slug
                channel	
                styleProperties
                created	
                components
                isFeatured
            }
        }
}
`;

function clone(obj: any): any {
    if (structuredClone) {
        return structuredClone(obj);
    }

    return obj ? JSON.stringify(obj) : obj;
}
